import type {Validations, WhatsappApi, WhatsappState} from 'src/types/whatsapp';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {EntityRelationship} from 'src/types/ats-entities';
import {
  normalizeNodeSchedule,
  parseNodeSchedule,
} from 'src/utils/node-schedule';


export {
  getSenderListWithItsEntityVariables,
  getSenderNameFromVariable,
} from 'src/utils/sms';

export const getSendFromOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Variable', 'Variable'), key: 'variable'},
  {
    label: t('Choose_Other', 'Choose Other'),
    key: 'choose_other',
  },
  {
    label: t('Default_Phone_Number', 'Default Phone Number'),
    key: 'default_phone_number',
  },
];

export const fallbackSenderPhoneList = [
  {
    key: '0',
    label: 'No Phone Numbers available',
    disabled: true,
  },
] as const;

export const fallbackSenderOption = [
  {
    key: '0',
    label: 'No Variables available',
    disabled: true,
  },
] as const;

export const normalizeWhatsapp = (
  whatsappState: WhatsappState,
): WhatsappApi => {
  const {
    to_number,
    from_number,
    external_source_id,
    selected_template,
    sender_category,
    body,
    variables,
  } = whatsappState;
  const nodeSchedule = normalizeNodeSchedule(whatsappState);
  return {
    task: {
      params: {
        to_number,
        from_number,
        external_source_id,
        selected_template,
        sender_category,
        template_id: selected_template?.id,
        body,
        variables,
      },
    },
    ...nodeSchedule,
  };
};

export const getErrors = (validationsObject: Validations): Array<string> => {
  const errors: Array<string> = [];
  const {
    senderNumberEmpty,
    sendToNumberEmpty,
    sameFromToNumber,
    invalidToNumber,
    placeholdersEmpty,
  } = validationsObject;
  if (senderNumberEmpty || sendToNumberEmpty || placeholdersEmpty) {
    errors.push('Please fill the required fields.');
  }
  if (sameFromToNumber) {
    errors.push('Sender Number cannot be same as Receiver Number.');
  }
  if (invalidToNumber) {
    errors.push('Send To Number is Invalid.');
  }
  return errors;
};

export const parseWhatsapp = (
  whatsappApiResponse: WhatsappApi,
): WhatsappState => {
  const whatsappSendTask = whatsappApiResponse.task;
  const taskDefinition = whatsappSendTask.params;
  if (!taskDefinition) {
    throw new Error('Whatsapp task definition not found');
  }

  const nodeSchedule = parseNodeSchedule(whatsappApiResponse);

  return {
    body: taskDefinition.body || '',
    external_source_id: taskDefinition.external_source_id || null,
    from_number: taskDefinition.from_number ?? null,
    to_number: formatToNumber(taskDefinition.to_number),
    sender_category: taskDefinition.sender_category,
    selected_template: taskDefinition.selected_template ?? {id: null},
    variables: taskDefinition.variables ?? null,
    ...nodeSchedule,
  };
};
const formatToNumber = (number: string | null | undefined) => {
  if (typeof number !== 'string') {
    return null;
  }

  const isWrappedInBraces = number.startsWith('{{') && number.endsWith('}}');
  return isWrappedInBraces ? number : `{{${number}}}`;
};
